/**
 * The two karcis booths, and the one hardware fact that separates them.
 *
 * `bahelastruk` shipped as a single flow because there was only ever one karcis:
 * a PC behind an analog TV, no touchscreen, no mouse a guest could reach. Every
 * design decision on that page follows from it — the welcome screen advances
 * itself on a 5-second timer because nobody can press anything, the operator
 * chrome sits unguarded in the corner because no guest's finger can get there,
 * and a session is exactly one photo because there is no way to ask for more.
 *
 * The Cikstik booth is an iPad. The guest CAN touch it, which turns every one
 * of those decisions from "the only option" into "the wrong option": a timer
 * marches past someone still reading the screen, and the unguarded corner is
 * the first thing a bored guest pokes. The requests in Dhafin's brief (tap to
 * start, several karcis per session, pick a frame) are all touch requests, and
 * all of them would break the TV booths if applied there.
 *
 * So the flow stays ONE component and the differences become data. Forking the
 * page would double every future fix on a booth that has already needed six
 * rounds of them (see karcisLayout.ts, and the print/QR history in Welcome).
 *
 * Which variant a project gets is decided by its `customPath`, in
 * `pages/view/customRoutes.tsx`:
 *
 *   bahelastruk        → KARCIS_TV     (all 7 live projects today; unchanged)
 *   bahelastruk-touch  → KARCIS_TOUCH  (Cikstik iPad; opt in by repointing)
 *
 * Nothing moves on its own: an existing booth keeps its exact behaviour until
 * somebody changes that project's customPath in the Studio.
 */

/** Ceiling on karcis printed per paid session, whatever the project asks for. */
export const KARCIS_MAX_TICKETS = 6

export type KarcisVariantId = 'tv' | 'touch'

export interface KarcisVariant {
  id: KarcisVariantId
  /**
   * Welcome advances to payment by itself after a few seconds.
   *
   * True on the TV booth because it has to be — there is no input device, so a
   * screen that waits for a tap waits forever. False on touch, where the guest
   * starting the session is the whole point (Dhafin: "bagian openingnya gausah
   * di timer jadi perlu dipencet kalo mau lanjut").
   */
  autoStartWelcome: boolean
  /**
   * `pages.capture.settings.captureCount` decides how many karcis one paid
   * session prints, each with its own photo.
   *
   * OFF for TV, and that is a safety property rather than a preference: all
   * seven live `bahelastruk` projects already carry a captureCount of 5 or 6
   * (inherited from the default config — nothing has ever read it on this
   * flow). Honouring it there would silently turn one Rp 5.000 payment into six
   * tickets on booths that are running right now.
   */
  honourCaptureCount: boolean
  /**
   * Put operator-only chrome behind the booth PIN.
   *
   * The thermal-printer settings button is a bare corner button. On the TV
   * booth that is unreachable; on an iPad it is one tap from a guest, next to
   * a payment bypass that already had to be PIN-gated for exactly this reason.
   */
  guardOperatorChrome: boolean
  /**
   * Offer "Punya voucher?" beside the QRIS quote.
   *
   * OFF for TV for the same reason `autoStartWelcome` is on there: a code has
   * to be TYPED, and that booth has no touchscreen and no keyboard a guest can
   * reach. Drawing the button there would advertise something the hardware
   * cannot do — worse than not offering it, because the guest then goes looking
   * for the team, which is the errand this feature exists to remove.
   *
   * On touch it is still not the only gate: the project's own
   * `checkout.settings.voucherEnabled` (falling back to `promoEnabled`, the
   * pairing every other brand reads) has to be on too, and so does the
   * `karcis_reward_voucher` feature flag.
   */
  allowVoucherEntry: boolean
}

export const KARCIS_TV: KarcisVariant = {
  id: 'tv',
  autoStartWelcome: true,
  honourCaptureCount: false,
  guardOperatorChrome: false,
  allowVoucherEntry: false
}

export const KARCIS_TOUCH: KarcisVariant = {
  id: 'touch',
  autoStartWelcome: false,
  honourCaptureCount: true,
  guardOperatorChrome: true,
  allowVoucherEntry: true
}

/**
 * How many karcis this session should print.
 *
 * Clamped rather than trusted: `captureCount` is a general booth setting whose
 * usual job is filling a frame's photo slots, so it carries values that make no
 * sense as a paper count (the platform default is 8). A booth that eats a roll
 * of thermal paper per guest is worse than one that prints too few.
 */
export function ticketsPerSession(
  variant: KarcisVariant,
  captureCount: number | undefined
): number {
  if (!variant.honourCaptureCount) return 1
  if (!Number.isFinite(captureCount) || (captureCount as number) < 1) return 1
  return Math.min(Math.floor(captureCount as number), KARCIS_MAX_TICKETS)
}

/**
 * Hard ceiling for a stage that waits on hardware, scaled by the work asked of
 * it. The supervisor writes a session off past this, so the budget has to grow
 * with the ticket count — otherwise a legitimate six-shot session is abandoned
 * mid-way and the guest has paid for karcis that never print.
 *
 * At one ticket both formulas return exactly the values the page shipped with
 * (90 s to capture, 180 s to print), so the TV booth's behaviour is unchanged.
 */
export function karcisStageCeilingMs(stage: number, tickets: number): number | undefined {
  const n = Math.max(1, tickets)
  switch (stage) {
    case 1:
      return 30_000
    case 3:
      return 60_000 + 30_000 * n
    case 4:
      return 120_000 + 60_000 * n
    case 5:
      return 60_000
    default:
      return undefined
  }
}
